import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { NSModal, NSField, NSSelect, NSSegment, NSChip, NSDrop } from '../shared/ns-kit'
import { I } from '../shared/ns-icons'

// Type chip label → backend category value
const REQ_TYPES = [
  { label: 'Content', value: 'content' },
  { label: 'Design', value: 'design' },
  { label: 'Bug fix', value: 'bug' },
  { label: 'New page', value: 'functionality' },
  { label: 'SEO', value: 'other' },
]
// Priority segment label → backend priority value
const PRIORITY_MAP = { Low: 'low', Normal: 'medium', High: 'high' }

// Client-facing "new request" modal — mirrors the admin NewRequestModal design.
export default function NewRequestModal({ onClose, onCreated }) {
  const queryClient = useQueryClient()
  const [siteId, setSiteId] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priorityLabel, setPriorityLabel] = useState('Normal')
  const [category, setCategory] = useState('content')
  const [files, setFiles] = useState([])

  const { data: sitesData } = useQuery('client-sites', () => apiService.get('/sites').then(r => r.data), { staleTime: 5 * 60 * 1000 })
  const sites = sitesData?.sites || (Array.isArray(sitesData) ? sitesData : [])
  const siteOptions = sites.map(s => ({ value: s.id, label: s.name }))

  // Default the site select to the first available site
  useEffect(() => {
    if (!siteId && sites.length) setSiteId(sites[0].id)
  }, [sites]) // eslint-disable-line react-hooks/exhaustive-deps

  const createMutation = useMutation(
    (payload) => apiService.post('/requests', payload, { headers: { 'Content-Type': 'multipart/form-data' } }),
    {
      onSuccess: () => {
        toast.success('Request submitted')
        queryClient.invalidateQueries('edit-requests')
        queryClient.invalidateQueries('client-requests')
        onCreated && onCreated()
        onClose()
      },
      onError: (e) => toast.error(e.response?.data?.error || e.response?.data?.errors?.[0]?.msg || 'Failed to submit request'),
    }
  )

  const submit = () => {
    if (!siteId) { toast.error('Select a site'); return }
    if (!title.trim()) { toast.error('Enter a title'); return }
    if (!description.trim()) { toast.error('Enter a description'); return }
    const fd = new FormData()
    fd.append('site_id', siteId)
    fd.append('title', title.trim())
    fd.append('description', description.trim())
    fd.append('priority', PRIORITY_MAP[priorityLabel] || 'medium')
    fd.append('category', category)
    if (files[0]) fd.append('screenshot', files[0])
    createMutation.mutate(fd)
  }

  return (
    <NSModal
      icon="message" tone="accent" onClose={onClose} width={640}
      title="New request" subtitle="Ask your team to make a change — we'll handle it for you."
      foot={
        <>
          <span style={{ fontSize: 11.5, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <I.bolt style={{ color: 'var(--accent-3)' }} /> Typical first reply in under 4 hours
          </span>
          <button className="btn" style={{ marginLeft: 'auto' }} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={createMutation.isLoading}>
            <I.plus /> {createMutation.isLoading ? 'Submitting…' : 'Submit request'}
          </button>
        </>
      }
    >
      <NSField label="Title">
        <input className="input" autoFocus value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Update the winter menu prices" />
      </NSField>

      {siteOptions.length > 1 && (
        <NSField label="Site">
          <NSSelect value={siteId} onChange={e => setSiteId(e.target.value)} options={siteOptions} />
        </NSField>
      )}

      <NSField label="Type">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {REQ_TYPES.map(t => <NSChip key={t.value} label={t.label} on={category === t.value} onClick={() => setCategory(t.value)} />)}
        </div>
      </NSField>

      <NSField label="Priority">
        <NSSegment options={['Low', 'Normal', 'High']} value={priorityLabel} onChange={setPriorityLabel} />
      </NSField>

      <NSField label="Description" hint="what should change & why">
        <textarea className="input" rows={4} style={{ resize: 'vertical', lineHeight: 1.6 }} value={description} onChange={e => setDescription(e.target.value)}
          placeholder="Describe the change in detail. Include page URLs, copy, or links to reference…" />
      </NSField>

      <NSField label="Attachments" hint="optional">
        <NSDrop icon="image" title={files.length ? `${files.length} file${files.length > 1 ? 's' : ''} selected` : 'Drop screenshots or files here'} hint="PNG, JPG, PDF · max 20 MB each" height={104} onFiles={fs => setFiles(fs)} />
      </NSField>
    </NSModal>
  )
}
